import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { approveRequest, rejectRequest } from "../../api/onboarding";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { ApprovalFields } from "./ApprovalFields";
import { RequestHeader } from "./RequestHeader";

export const RequestCard = ({ request, branches, reload }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const single = request.role === "employee";
  const [form, setForm] = useState({ branch_ids: request.branch_ids || [], job_title: request.job_title || "", basic_salary: "" });

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleBranch = (id) =>
    setForm((f) => (single
      ? { ...f, branch_ids: f.branch_ids[0] === id ? [] : [id] }
      : { ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((b) => b !== id) : [...f.branch_ids, id] }));

  const act = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      reload();
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const approve = () => {
    if (!form.branch_ids.length) {
      toast.error(t("requests.branchRequired"));
      return;
    }
    return act(
      () =>
        approveRequest(request.id, {
          branch_ids: form.branch_ids,
          job_title: form.job_title || null,
          basic_salary: form.basic_salary === "" ? null : Number(form.basic_salary),
        }),
      t("requests.approvedOnboarding")
    );
  };

  return (
    <NeuCard className="animate-rise-in p-5" data-testid={`request-card-${request.id}`}>
      <RequestHeader request={request} />
      <ApprovalFields form={form} setField={setField} branches={branches} toggleBranch={toggleBranch} single={single} />
      <NeuInput
        className="mt-4"
        data-testid={`request-reason-${request.id}`}
        placeholder={t("requests.reasonPlaceholder")}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="mt-3 flex gap-2">
        <NeuButton
          variant="primary"
          disabled={busy}
          data-testid={`request-approve-${request.id}`}
          className="flex items-center gap-2"
          onClick={approve}
        >
          <Check className="h-4 w-4" /> {t("requests.approve")}
        </NeuButton>
        <NeuButton
          variant="danger"
          disabled={busy}
          data-testid={`request-reject-${request.id}`}
          className="flex items-center gap-2"
          onClick={() => act(() => rejectRequest(request.id, reason), t("requests.rejected"))}
        >
          <X className="h-4 w-4" /> {t("requests.reject")}
        </NeuButton>
      </div>
    </NeuCard>
  );
};
